import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import compact from 'lodash/compact'
import range from 'lodash/range'
import Tether from 'tether'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import createAutorun from 'modules/core/hooks/createAutorun'
import createComputed from 'modules/core/hooks/createComputed'
import i18n from 'modules/core/lib/i18n'

import type { FieldType } from 'modules/reports/components/Editor/Field/types'
import { TYPES, argumentsByOperator } from 'modules/reports/lib/Constants'
import type ConditionForm from 'modules/reports/lib/Store/FormCollections/Filter/Condition/ConditionForm'

import type { ValueType } from './ArgumentForm'
import ArgumentForm from './ArgumentForm'
import OperatorSelector from './OperatorSelector'
import ConditionPicker from './_ConditionPicker'
import { OperatorForm } from './forms'

interface Props {
  fieldType: FieldType
  onClose: () => void
  variables: any
  onToggleNestedPopover: (open: boolean) => void
}

const valueType = (fieldType: FieldType, operator: string, usingVariable: boolean): ValueType => {
  if (usingVariable) {
    return 'string'
  }

  const isDate = fieldType.field?.attributes.type === TYPES.date
  const isRelativeOffsetDate = operator.match(/^last_/)

  return isDate && !isRelativeOffsetDate ? 'date' : 'string'
}

const isValid = (forms: ArgumentForm[], operatorForm: OperatorForm): boolean => {
  const values = compact(forms.map((f) => f.data().value))
  const operator = operatorForm.get('value').value
  const numOfArgs = argumentsByOperator(operator)

  return operator && values.length === numOfArgs
}

const reactiveGenerateForms = createAutorun(
  (fieldType: FieldType, operatorForm: OperatorForm, formData: string, setForms: any) => {
    const forms = generateForms(fieldType, operatorForm, formData, false)
    setForms(forms)
  }
)

/**
 * Dear developer, we're reaching technical debt bankruptcy with how we're
 * managing Reports query builder forms. That's why this is so complex. In the
 * near future we should rewrite all this logic in a more maintainable way in
 * order to keep adding features here.
 */
const generateForms = (
  fieldType: FieldType,
  operatorForm: OperatorForm,
  formData: string,
  startOver: boolean
) => {
  const formsWithVar: any[] = JSON.parse(formData)
  const operator = operatorForm.get('value').value

  if (!operator || !fieldType.form) {
    return []
  }

  const argumentTypes = formsWithVar.map((fw: any) =>
    valueType(fieldType, operator, !!fw.use_variable)
  )

  // First render
  const numOfArgs = argumentsByOperator(operator)
  const quantity = range(numOfArgs)

  const initialForms = quantity.map((_val) => {
    return {
      value: '',
      use_variable: false,
    }
  })

  const forms = () => {
    // We need to start again each time we change operator
    if (startOver) {
      return initialForms
    }

    // When you're modifying the form values
    if (formsWithVar.length) {
      return formsWithVar
    }

    // The form comes from the API
    const form = fieldType.form as ConditionForm
    if (form.arguments?.length) {
      return form.arguments.slice()
    }

    return initialForms
  }

  const generatedForms = ArgumentForm.buildForms(
    forms(),
    argumentTypes // 'date' || 'string'
  )

  return generatedForms
}

const ComputedOperatorForm = createComputed(
  (operator: any) =>
    new OperatorForm({
      value: operator,
    })
)

const ComputedFormsData = createComputed((forms: ArgumentForm[]) => {
  return forms.map((f) => f.data())
})

const FilteringOptions = ({ fieldType, onToggleNestedPopover, variables, onClose }: Props) => {
  const [forms, setForms] = React.useState<ArgumentForm[]>([])
  React.useEffect(() => {
    // This hack fixes weird tether arrow position
    Tether.position()
  })

  const form = fieldType.form as ConditionForm

  const originalOperator = form.get('operator').value
  const operatorForm = ComputedOperatorForm(originalOperator).get()
  const formData = JSON.stringify(ComputedFormsData(forms).get())
  reactiveGenerateForms(fieldType, operatorForm, formData, setForms)

  const showConditions = !!operatorForm.get('value').value && forms.length > 0

  const onContinue = () => {
    if (isValid(forms, operatorForm)) {
      form.updateWithArgs(
        {
          operator: operatorForm.get('value').value,
        },
        ArgumentForm.convertToParams(forms)
      )
    }

    onClose()
  }

  return (
    <>
      <FormRow>
        <OperatorSelector
          data-testid="selector"
          fieldType={fieldType}
          form={operatorForm}
          onChange={() => {
            const forms = generateForms(fieldType, operatorForm, formData, true)
            setForms(forms)
          }}
        />
      </FormRow>
      {showConditions && (
        <ConditionPicker
          variables={variables}
          fieldType={fieldType}
          forms={forms}
          operatorForm={operatorForm}
          onToggleNestedPopover={onToggleNestedPopover}
        />
      )}
      <FormRow>
        <Button disabled={!isValid(forms, operatorForm)} onClick={onContinue}>
          {i18n.t('common.continue')}
        </Button>
      </FormRow>
    </>
  )
}

export default observer(FilteringOptions)
